import { useNavigate } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES, ORDER_BY_NUMBER_QUERY_PARAM, PAGE_SIZE } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useMemo, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import {
    ActuatorsPageData,
    ActuatorsPageVariables,
    getActuatorsPageConfig,
} from 'src/modules/actuators/ActuatorsPageMapping.ts';
import { skipToken } from '@apollo/client';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { ActuatorsFilterPagePrimaryQueryVariables, DirectionEnum, RegionsEnum } from 'src/generated/operations.ts';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { DeviceGrid } from 'src/common/DeviceGrid/DeviceGrid';
import { Device } from 'src/common/Device/Device';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { prepareActuatorAttributes } from 'src/modules/actuators/ActuatorCard/prepareActuatorAttributes';
import clsx from 'clsx';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import { ValvesSelectActuatorButton } from 'src/modules/actuators/components/ValvesSelectActuatorButton';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { updatedLocationState } from 'src/common/updatedLocationState.ts';
import { updateFilter } from 'src/common/updateFilter.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { Price } from 'src/common/Price/Price.tsx';
import { useListDisplayMode } from 'src/common/hooks/useListDisplayMode';

const limit = PAGE_SIZE;

export const ActuatorsPage = () => {
    const { userSettings, currency } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const pageConfig = useMemo(() => getActuatorsPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }
    const { state: userInputState, searchParams, locationState, setLocationState } = useUserInputState([]);

    const [isListDisplayMode, toggleDisplayMode] = useListDisplayMode();
    const view = isListDisplayMode ? 'list' : 'grid';

    const [
        filter,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.filterConfig,
    ], searchParams);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const navigate = useNavigate();

    const hasAdditionalFilter = useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: pageConfig.filterConfig,
    });

    const [
        actuatorsFilter,
    ] = useFilterLogic(pageConfig.filterConfig, searchParams);

    const currentPage = parseInt(locationState.actuatorsPage, 10) || 1;

    const [orderByNumber, setOrderByNumber] = useOrderBy(`${ORDER_BY_NUMBER_QUERY_PARAM}_a`);

    const setActuatorPage = useCallback((newSearchParams: URLSearchParams, newPage: number) => updatedLocationState(
        newSearchParams,
        {
            ...locationState,
            actuatorsPage: newPage.toString(),
        },
    ), [locationState]);

    const [setPage, onPageChange] = usePageChange(() => PATHS.Actuators, setActuatorPage);

    const actuatorsCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        const newFilter = checkboxHandler(detail);
        setPage(1, updateFilter(newFilter, searchParams));
        return newFilter;
    }, [setPage, checkboxHandler, searchParams]);

    const resetActuatorsFilters = useCallback(() => {
        setPage(1, filtersReset(pageConfig.filterConfig, searchParams));
    }, [setPage, filtersReset, pageConfig.filterConfig, searchParams]);

    const resetBreadcrumb = useCallback(() => {
        setLocationState({
            ...locationState,
            actuatorLabel: '',
        });
    }, [locationState, setLocationState]);

    const variables = useMemo((): { variables: ActuatorsPageVariables } | typeof skipToken => {
        if (actuatorsFilter === null) {
            return skipToken;
        }

        const offset = (currentPage - 1) * limit;
        return {
            variables: {
                offset,
                limit,
                filter: composeFilter(
                    pageConfig.filterConfig,
                    actuatorsFilter,
                    userSettings.currentLocale,
                ),
                locale: userSettings.currentLocale,
                label: locationState.valveLabel,
                orderByNumber,
            },
        };
    }, [locationState, orderByNumber, currentPage, actuatorsFilter, userSettings, pageConfig]);

    const defVariables = useDeferredValue(variables);
    const isStale = defVariables !== variables;

    const filtersVariables = useMemo((): { variables: ActuatorsFilterPagePrimaryQueryVariables } | typeof skipToken => {
        if (actuatorsFilter === null) {
            return skipToken;
        }

        return {
            variables: {
                filter: composeFilter(
                    pageConfig.filterConfig,
                    actuatorsFilter,
                    userSettings.currentLocale,
                ),
                locale: userSettings.currentLocale,
                label: locationState.valveLabel,
                region: userSettings.currentRegion,
            },
        };
    }, [actuatorsFilter, pageConfig, userSettings, locationState]);

    const [isFilterOpen, setFilterOpen] = useState(false);

    return (
        <MainLayout>
            <MainLayoutHeader
                titleId={MESSAGES.actuatorsPageTitle}
                navigation={null}
                breadcrumbs={(
                    <ValveBreadcrumbs
                        currentPage={PATHS.Actuators}
                        homeValue={homePageValue}
                        userInputState={userInputState}
                        locationState={locationState}
                        searchParams={searchParams}
                        resetHandler={locationState.actuatorLabel ? resetBreadcrumb : undefined}
                    />
                )}
                selectionButton={!isRevitPlugin && (
                    <SelectionsButton
                        isMobile={isMobile}
                        searchParams={searchParams}
                        locationState={locationState}
                    />
                )}
            />

            <Suspense fallback={<SkeletonPageLayout/>}>
                <Query<ActuatorsPageData, ActuatorsPageVariables>
                    query={pageConfig.query}
                    variables={defVariables}
                >
                    {({ data }) => {
                        if (data === undefined) {
                            return (
                                <SkeletonPageLayout/>
                            );
                        }

                        if (data.vssActuators.products.length === 0) {
                            return (
                                <div className={clsx({
                                    'vss-actuators-page': true,
                                    'vss-actuators-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        label={MESSAGES.modalFilterActuatorsLabel}
                                        filterConfigPrimary={pageConfig.filterConfigPrimary}
                                        filterConfigSecondary={pageConfig.filterConfigSecondary}
                                        queryPrimary={pageConfig.queryPrimary}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={actuatorsCheckboxHandler}
                                        handleReset={resetActuatorsFilters}
                                        filter={filter}
                                        isFilterSidebarOpened={isFilterOpen}
                                        setFilterSidebarOpened={setFilterOpen}
                                    />
                                    <NoData/>
                                </div>
                            );
                        }

                        return (
                            <div className={clsx({
                                'vss-actuators-page': true,
                                'vss-actuators-page--mobile': isMobile,
                            })}
                            >
                                <FilterSidebarComponent
                                    label={MESSAGES.modalFilterActuatorsLabel}
                                    filterConfigPrimary={pageConfig.filterConfigPrimary}
                                    filterConfigSecondary={pageConfig.filterConfigSecondary}
                                    queryPrimary={pageConfig.queryPrimary}
                                    genericFilterQuery={pageConfig.genericFilterQuery}
                                    variables={filtersVariables}
                                    checkboxHandler={actuatorsCheckboxHandler}
                                    handleReset={resetActuatorsFilters}
                                    filter={filter}
                                    isFilterSidebarOpened={isFilterOpen}
                                    setFilterSidebarOpened={setFilterOpen}
                                />

                                <div className={clsx({
                                    'vss-actuators-page__list': true,
                                    'vss-actuators-page__list--stale': isStale,
                                })}
                                >
                                    <DeviceGrid
                                        isMobile={isMobile}
                                        hasAdditionalFilter={hasAdditionalFilter}
                                        currentPage={currentPage}
                                        pageSize={limit}
                                        total={data?.vssActuators.count || 0}
                                        onPageChange={(p: number) => onPageChange(p, searchParams)}
                                        defaultOrderLabel={MESSAGES.Label}
                                        onToggleView={toggleDisplayMode}
                                        view={view}
                                        orderItems={
                                            generateOrderItems(
                                                [
                                                    'runningTime',
                                                    'torque',
                                                    'actuatingForce',
                                                ],
                                                pageConfig?.orderItemsConfig,
                                                DirectionEnum.Asc,
                                            )
                                        }
                                        orderSelected={orderByNumber}
                                        onOrderByChange={(newOrder) => {
                                            setPage(1, setOrderByNumber(searchParams, newOrder));
                                        }}
                                        filterHandler={() => setFilterOpen(true)}
                                    >
                                        {data?.vssActuators.products.map(({ product, type }) => {
                                            const price = getPriceValue(
                                                product,
                                                userSettings.shoppingCenter as keyof typeof product,
                                                currency,
                                            );
                                            return (
                                                <Device
                                                    disableProductLink={userSettings.currentRegion === RegionsEnum.Am}
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ActuatorIcon/>}
                                                    product={product}
                                                    view={isMobile ? 'mobile' : view}
                                                    chip={type === 1 ? (
                                                        <div className="vss-device__recommendation">
                                                            <I18n messageId={MESSAGES.recommended}/>
                                                        </div>
                                                    ) : ''}
                                                    attributes={prepareActuatorAttributes(product)}
                                                    price={userSettings.currentRegion !== RegionsEnum.Am ? (
                                                        <Price
                                                            price={price.value}
                                                            currency={price.currency}
                                                            locale={userSettings.currentLocale}
                                                        />
                                                    ) : null}
                                                    selectButton={(
                                                        <ValvesSelectActuatorButton
                                                            searchParams={updatedLocationState(
                                                                new URLSearchParams(searchParams.toString()),
                                                                {
                                                                    ...locationState,
                                                                    actuatorLabel: product.Label,
                                                                    combo: null,
                                                                    selectedAccessories: [],
                                                                    pipeConnectors: false,
                                                                    checkoutComment: '',
                                                                },
                                                            )}
                                                            fullWidth={isMobile}
                                                        />
                                                    )}
                                                />
                                            );
                                        })}
                                    </DeviceGrid>
                                </div>
                            </div>
                        );
                    }}
                </Query>
            </Suspense>
        </MainLayout>
    );
};
